import { useCallback, useMemo } from 'react';

import { SettingsGearIcon } from '@braskai/icons';
import { useLocation } from 'react-router-dom';

import { Stack } from '@mui/material';

import { useTeamspaceSettingsModal } from '@/features/teamspace';

import { useBalanceStore } from '@/entities/balance';
import { useGetProjects } from '@/entities/project';
import { useCurrentSpaceId } from '@/entities/teamspace';
import { useFeatureFlags, useIsDemoView } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { inIframe } from '@/shared/lib/browser';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { DesktopNavigation } from '@/shared/ui/DesktopNavigation';
import { Logo } from '@/shared/ui/Logo';
import { SidebarButton } from '@/shared/ui/SidebarButton';

import { getHelpNavOptions, getNavOptions } from '../../lib/getNavOptions';
import { ApiBannerSidebar } from '../ApiBannerSidebar';
import { ProfileItem } from '../ProfileItem';
import { TeamspacesNav } from '../TeamspacesNav';
import { TrialSidebarBanner } from '../TrialSidebarBanner';

type DesktopNavProps = {
  isOpen?: boolean;
  locked?: boolean;
  handleCloseSidebar?: () => void;
  handleLocked?: () => void;
};

export const DesktopNav = ({ isOpen, locked, handleCloseSidebar, handleLocked }: DesktopNavProps) => {
  const { pathname } = useLocation();

  const { featureVisible: isVoicePresetsFeatureVisible } = useFeatureFlags('voice_presets');
  const { featureVisible: isGlossaryFeatureVisible } = useFeatureFlags('glossary');
  const openTeamspaceSettingsModal = useTeamspaceSettingsModal((state) => state.open);
  const planCategory = useBalanceStore((state) => state.planCategory);
  const { hasTrialPlan } = useBalanceStore();

  const currentSpaceId = useCurrentSpaceId();
  const { data: getProjectsResponse } = useGetProjects({ enabled: false });

  const hasProjects = useMemo(() => Boolean(getProjectsResponse?.data?.length), [getProjectsResponse?.data]);

  const { isDemoView } = useIsDemoView();

  const mainNavigation = getNavOptions({
    isGlossaryFeatureVisible,
    isVoicePresetsFeatureVisible,
    pathname,
    planCategory,
    spaceId: currentSpaceId,
  });

  const bottomNavigation = getHelpNavOptions({ isWhiteLabel: IS_WHITE_LABEL, planCategory });

  const handleSettingsClick = useCallback(() => {
    MixpanelEvents.settingsAndMembersClick();
    openTeamspaceSettingsModal();
    handleCloseSidebar?.();
  }, [handleCloseSidebar, openTeamspaceSettingsModal]);

  const topNavigation = useMemo(
    () => [
      {
        title: 'Settings & members',
        dataTestId: 'sidebar-menu-settings',
        icon: <SettingsGearIcon height={16} width={16} />,
        onClick: handleSettingsClick,
      },
    ],
    [handleSettingsClick],
  );

  const opened = isOpen || locked;

  const isTiktok = inIframe();

  return (
    <DesktopNavigation
      logoContainer={
        <Logo
          isCompact={!opened}
          isWhiteLabel={IS_WHITE_LABEL}
          target={isTiktok ? undefined : '_blank'}
          to={isTiktok ? '/' : 'https://www.rask.ai/'}
        >
          <SidebarButton locked={locked} onClick={handleLocked} />
        </Logo>
      }
      bottomNavigation={bottomNavigation}
      isOpen={opened}
      mainNavigation={mainNavigation}
      profileContainer={<ProfileItem active={pathname === PATHS.account} />}
      promoNavItems={undefined}
      teamspaceContainer={<TeamspacesNav opened={opened} handleCloseSidebar={handleCloseSidebar} />}
      topNavigation={topNavigation}
    >
      {opened && (
        <Stack margin="0 12px">
          {!isDemoView && !hasTrialPlan && <ApiBannerSidebar />}
          {isDemoView && hasProjects && <TrialSidebarBanner />}
        </Stack>
      )}
    </DesktopNavigation>
  );
};
